/**
 * ScannerModal Component
 * Barcode scanner modal using HTML5-QRCode
 */

import { useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

interface ScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (barcode: string) => void;
  error: string | null;
}

export default function ScannerModal({ isOpen, onClose, onScan, error }: ScannerModalProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerElementRef = useRef<HTMLDivElement>(null);

  const startScanner = async () => {
    if (!scannerElementRef.current) return;

    try {
      const html5QrCode = new Html5Qrcode('scanner-container');
      scannerRef.current = html5QrCode;

      // Calculate responsive qrbox size based on container
      const container = scannerElementRef.current;
      const containerWidth = container.clientWidth || 400;
      const containerHeight = container.clientHeight || 200;

      // Use 80% of container width and maintain rectangular aspect ratio
      const qrboxWidth = Math.min(Math.floor(containerWidth * 0.8), 300);
      const qrboxHeight = Math.min(Math.floor(containerHeight * 0.8), 180);

      // Start scanning with camera
      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: qrboxWidth, height: qrboxHeight },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          onScan(decodedText);
        },
        (_errorMessage) => {
          // Ignore scanning errors (they're frequent during scanning)
        }
      );
    } catch (err) {
      console.error('Scanner error:', err);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
        scannerRef.current = null;
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
  };

  useEffect(() => {
    if (isOpen && scannerElementRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        startScanner();
      }, 100);
    } else if (!isOpen && scannerRef.current) {
      stopScanner();
    }

    // Cleanup on unmount
    return () => {
      if (scannerRef.current) {
        stopScanner();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Scan Barcode</h3>
          <button
            onClick={() => {
              stopScanner();
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div
          ref={scannerElementRef}
          id="scanner-container"
          className="w-full bg-gray-100 rounded-lg overflow-hidden"
          style={{ minHeight: '200px' }}
        />

        <div className="mt-4 flex gap-2">
          <button
            onClick={() => {
              stopScanner();
              onClose();
            }}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

